import React, { useEffect, useRef, useState, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import { useVisitorSignalR, VisitorSession } from "@/hooks/useVisitorSignalR";

interface DayCount { date: string; count: number; }
interface Breakdown { name: string; count: number; pct: number; }

interface VisitorStats {
  onlineNow: number;
  visitsToday: number;
  visitsThisMonth: number;
  last7Days: DayCount[];
  browserBreakdown: Breakdown[];
  osBreakdown: Breakdown[];
  countryBreakdown: Breakdown[];
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatReferrer(referrer: string | null): string {
  if (!referrer) return "Direct";
  try { return new URL(referrer).hostname; }
  catch { return referrer.slice(0, 40); }
}

function formatPage(url: string | null): string {
  if (!url) return "—";
  try {
    const u = new URL(url);
    return u.pathname + (u.search || "");
  } catch { return url.slice(0, 60); }
}

function countryFlag(code: string | null): string {
  if (!code || code.length !== 2) return "";
  const c = code.toUpperCase();
  return String.fromCodePoint(127397 + c.charCodeAt(0)) + String.fromCodePoint(127397 + c.charCodeAt(1));
}

function getIdleMinutes(lastSeen: string): number {
  return Math.floor((Date.now() - new Date(lastSeen).getTime()) / 60000);
}

type PageContext = { label: string; color: string } | null;

function getPageContext(url: string | null): PageContext {
  if (!url) return null;
  let path = url;
  try { path = new URL(url).pathname; } catch { /* use raw */ }
  if (/\/checkouts?\//i.test(path) || /\/checkout/i.test(path)) return { label: "In Checkout", color: "text-amber-700 bg-amber-50 border-amber-200" };
  if (/\/cart/i.test(path)) return { label: "Cart", color: "text-orange-700 bg-orange-50 border-orange-200" };
  if (/\/products?\//i.test(path)) return { label: "Product", color: "text-purple-700 bg-purple-50 border-purple-200" };
  if (/\/collections?\//i.test(path)) return { label: "Collection", color: "text-indigo-700 bg-indigo-50 border-indigo-200" };
  if (/\/search/i.test(path)) return { label: "Search", color: "text-sky-700 bg-sky-50 border-sky-200" };
  return null;
}

function Sparkline({ data }: { data: DayCount[] }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const W = 180, H = 36;
  const step = W / (data.length - 1 || 1);
  const pts = data.map((d, i) => `${i * step},${H - (d.count / max) * H}`).join(" ");
  const area = `0,${H} ` + data.map((d, i) => `${i * step},${H - (d.count / max) * H}`).join(" ") + ` ${(data.length - 1) * step},${H}`;
  return (
    <svg width={W} height={H} className="block">
      <defs>
        <linearGradient id="spark-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#spark-grad)" />
      <polyline points={pts} fill="none" stroke="#6366f1" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-1 min-w-[130px]">
      <span className={`text-3xl font-bold tabular-nums ${accent ?? "text-slate-900"}`}>{value}</span>
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      {sub && <span className="text-xs text-slate-400">{sub}</span>}
    </div>
  );
}

function BreakdownBar({ items, title, showFlags }: { items: Breakdown[]; title: string; showFlags?: boolean }) {
  if (!items.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex-1 min-w-[160px]">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{title}</p>
      <div className="space-y-2.5">
        {items.slice(0, 6).map(b => (
          <div key={b.name}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-slate-600 truncate max-w-[120px]">
                {showFlags && <span className="mr-1">{countryFlag(b.name)}</span>}
                {b.name || "Unknown"}
              </span>
              <span className="text-slate-400 ml-2 shrink-0">{b.count} <span className="text-slate-300">({b.pct}%)</span></span>
            </div>
            <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div className="bg-primary-hover h-1.5 rounded-full transition-all duration-500" style={{ width: `${Math.min(b.pct, 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VisitorMonitor() {
  const { subscription } = useSubscriptions();
  const api = useApi();
  const [sessions, setSessions] = useState<VisitorSession[]>([]);
  const [stats, setStats] = useState<VisitorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [signalRConnected, setSignalRConnected] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [visitorLimit, setVisitorLimit] = useState<number>(50);
  const fallbackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const planLimit = subscription?.visitorMonitorMaxVisible ?? 50;

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.messaging.get("Visitor/active");
      const data = await res.json();
      if (data?.enabled === false) return;
      if (Array.isArray(data?.sessions)) setSessions(data.sessions);
      if (data?.visitorLimit) setVisitorLimit(data.visitorLimit);
      setLastRefresh(new Date());
    } catch { /* non-fatal */ }
  }, [api]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.messaging.get("Visitor/stats");
      const data = await res.json();
      if (data?.enabled !== false) setStats(data);
    } catch { /* stats optional */ }
  }, [api]);

  // SignalR: merge incoming session into state (upsert by id)
  const handleVisitorUpdate = useCallback((session: VisitorSession) => {
    setSessions(prev => {
      const idx = prev.findIndex(s => s.id === session.id || s.sessionId === session.sessionId);
      const next = idx >= 0
        ? prev.map((s, i) => i === idx ? session : s)
        : [session, ...prev].slice(0, planLimit);
      setLastRefresh(new Date());
      return next;
    });
  }, [planLimit]);

  useVisitorSignalR({
    enabled: !!subscription?.enableVisitorTracking,
    onUpdate: handleVisitorUpdate,
    onConnected: () => {
      setSignalRConnected(true);
      // Kill fallback polling — SignalR is live
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    },
    onDisconnected: () => {
      setSignalRConnected(false);
      // Restore 30s fallback polling if SignalR drops
      if (!fallbackIntervalRef.current) {
        fallbackIntervalRef.current = setInterval(loadSessions, 30_000);
      }
    },
  });

  useEffect(() => {
    if (!subscription?.enableVisitorTracking) { setLoading(false); return; }

    // Initial load always via REST (SignalR delivers diffs, not initial state)
    Promise.all([loadSessions(), loadStats()]).finally(() => setLoading(false));

    // Stats refresh every 60s (not via SignalR — aggregate data, not per-session)
    statsIntervalRef.current = setInterval(loadStats, 60_000);

    // Fallback polling starts immediately; killed once SignalR connects
    fallbackIntervalRef.current = setInterval(loadSessions, 30_000);

    return () => {
      if (fallbackIntervalRef.current) clearInterval(fallbackIntervalRef.current);
      if (statsIntervalRef.current) clearInterval(statsIntervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.enableVisitorTracking]);

  if (!subscription?.enableVisitorTracking) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-24 text-center px-6">
        <div className="w-14 h-14 rounded-2xl bg-primary-bg flex items-center justify-center mb-4">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-primary">
            <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Visitor Monitor</h2>
        <p className="text-sm text-slate-500 max-w-xs">See who's on your site in real time — pages visited, time on site, browser, and country. Available on upgraded plans.</p>
      </div>
    );
  }

  const chatting = sessions.filter(s => s.isChatting).length;
  const browsing = sessions.filter(s => !s.isChatting).length;
  const IDLE_THRESHOLD = 10;
  const effectiveLimit = visitorLimit || planLimit;

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {/* Status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {sessions.length > 0 ? (
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {sessions.length} live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 text-xs font-medium px-2.5 py-1 rounded-full">
              0 active
            </span>
          )}
          {signalRConnected ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Real-time
            </span>
          ) : (
            <span className="text-xs text-slate-400">
              {lastRefresh
                ? `Updated ${lastRefresh.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · auto-refreshes every 30s`
                : "Loading…"}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400">
          Showing up to {effectiveLimit} visitors · plan cap {planLimit}
        </span>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="flex flex-wrap gap-4">
          <StatCard label="Online Now" value={stats.onlineNow} accent={stats.onlineNow > 0 ? "text-emerald-600" : "text-slate-900"} />
          {chatting > 0 && <StatCard label="Chatting" value={chatting} accent="text-primary" />}
          {browsing > 0 && <StatCard label="Browsing" value={browsing} />}
          <StatCard label="Visits Today" value={stats.visitsToday.toLocaleString()} />
          <StatCard label="This Month" value={stats.visitsThisMonth.toLocaleString()} />
          {stats.last7Days?.length > 1 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-2 flex-1 min-w-[220px]">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Last 7 Days</span>
              <Sparkline data={stats.last7Days} />
              <div className="flex justify-between text-[10px] text-slate-300">
                <span>{stats.last7Days[0]?.date?.slice(5)}</span>
                <span>{stats.last7Days[stats.last7Days.length - 1]?.date?.slice(5)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Breakdown bars */}
      {stats && (stats.browserBreakdown.length > 0 || stats.osBreakdown.length > 0 || stats.countryBreakdown.length > 0) && (
        <div className="flex flex-wrap gap-4">
          <BreakdownBar items={stats.browserBreakdown} title="Browser" />
          <BreakdownBar items={stats.osBreakdown} title="OS" />
          <BreakdownBar items={stats.countryBreakdown} title="Country" showFlags />
        </div>
      )}

      {/* Live session table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700">Active Visitors</p>
          {sessions.some(s => getIdleMinutes(s.lastSeen) >= IDLE_THRESHOLD) && (
            <span className="text-xs text-slate-400">Dimmed rows = idle {IDLE_THRESHOLD}+ min (still within 30-min timeout)</span>
          )}
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">No active visitors right now — check back in a moment.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Current Page</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Context</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">From</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Browser</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Country</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Pages</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Time on Site</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sessions.map((s) => {
                  const idleMin = getIdleMinutes(s.lastSeen);
                  const isIdle = idleMin >= IDLE_THRESHOLD;
                  const pageCtx = getPageContext(s.currentPage);
                  const flag = countryFlag(s.country);
                  return (
                    <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${isIdle ? "opacity-50" : ""}`}>
                      <td className="px-4 py-3">
                        {s.isChatting ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary-bg px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />Chatting
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Browsing
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <span className="block truncate text-slate-700 text-xs font-mono" title={s.currentPage ?? ""}>{formatPage(s.currentPage)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {pageCtx && (
                          <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${pageCtx.color}`}>
                            {pageCtx.label}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{formatReferrer(s.referrer)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{s.browser ?? "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {flag && <span className="mr-1">{flag}</span>}
                        {s.country ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-slate-600 font-medium">{s.pageCount}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{formatDuration(s.secondsOnSite)}</td>
                      <td className="px-4 py-3 text-xs tabular-nums">
                        {isIdle ? (
                          <span className="text-slate-400">Idle {idleMin}m</span>
                        ) : (
                          <span className="text-emerald-500">{idleMin === 0 ? "Active" : `${idleMin}m ago`}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
